/**
 * TaskUpdateStore — data-access for per-task standup updates that power the
 * Daily Scrum roll-up.
 *
 * Strict layering: talks to the API only through the {@link EngineeringVectorApi}
 * facade (`api.documents<TaskUpdate>('taskUpdates')` + `api.identityProvider`).
 *
 * Persistence model: each update is one document with id `update_<uuid>`. The
 * renderable fields (roomId, taskId, author, completed, next, blocker, status,
 * createdAt, deleted) are mirrored onto the DDB pointer row's index attributes
 * so the Daily Scrum renders directly from pointer rows WITHOUT hydrating S3.
 *
 * Scoping: {@link listByRoom} uses a roomId-filtered listMetaInfo query and
 * {@link listByTask} a taskId-filtered one (both mirrored as index attributes),
 * each with a defensive client-side re-filter.
 */

import type { EngineeringVectorApi } from '../../../api';
import type { DocumentStore } from '../../../api';
import type { StringMap } from '../../../api';
import type { ListMetaInfoQuery } from '../../../api';
import type {
  TaskUpdate,
  TaskUpdateDraft,
  UpdateStatus,
} from '../models/types';
import { taskUpdateDocId } from '../models/sprintboardModel';

export const TASK_UPDATES_COLLECTION = 'taskUpdates';

const VALID_STATUSES: UpdateStatus[] = ['on_track', 'at_risk', 'blocked'];

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  roomId: 'roomId',
  taskId: 'taskId',
  author: 'author',
  completed: 'completed',
  next: 'next',
  blocker: 'blocker',
  status: 'status',
  createdAt: 'createdAt',
  deleted: 'deleted',
} as const;

export class TaskUpdateStore {
  private readonly docs: DocumentStore<TaskUpdate>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<TaskUpdate>(TASK_UPDATES_COLLECTION);
  }

  private indexAttributes(u: TaskUpdate): StringMap {
    return {
      [ROW.roomId]: u.roomId,
      [ROW.taskId]: u.taskId,
      [ROW.author]: u.author ?? '',
      [ROW.completed]: u.completed ?? '',
      [ROW.next]: u.next ?? '',
      [ROW.blocker]: u.blocker ?? '',
      [ROW.status]: u.status ?? '',
      [ROW.createdAt]: u.createdAt ?? '',
      [ROW.deleted]: u.deleted ? 'true' : 'false',
    };
  }

  private toUpdate(row: StringMap): TaskUpdate {
    const status = row[ROW.status] ?? '';
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      roomId: row[ROW.roomId] ?? '',
      taskId: row[ROW.taskId] ?? '',
      author: row[ROW.author] ?? '',
      completed: row[ROW.completed] ?? '',
      next: row[ROW.next] ?? '',
      blocker: row[ROW.blocker] || undefined,
      status: VALID_STATUSES.includes(status as UpdateStatus)
        ? (status as UpdateStatus)
        : undefined,
      createdAt: row[ROW.createdAt] ?? '',
      deleted: row[ROW.deleted] === 'true',
    };
  }

  /** Lists a room's non-deleted task updates (Daily Scrum feed). */
  async listByRoom(roomId: string): Promise<TaskUpdate[]> {
    if (!roomId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#roomId = :roomId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#roomId': ROW.roomId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':roomId': roomId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASK_UPDATES_COLLECTION &&
          r[ROW.roomId] === roomId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toUpdate(r));
  }

  /** Lists a single task's non-deleted updates (task history / detail view). */
  async listByTask(taskId: string): Promise<TaskUpdate[]> {
    if (!taskId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#taskId = :taskId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#taskId': ROW.taskId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':taskId': taskId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASK_UPDATES_COLLECTION &&
          r[ROW.taskId] === taskId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toUpdate(r));
  }

  /** Loads a single update document by id. */
  async get(id: string): Promise<TaskUpdate | undefined> {
    return this.docs.get(id);
  }

  /**
   * Lists ALL non-deleted task updates across every room. Used by cross-room
   * views (e.g. the AI Team Digest's standup-highlights section) that reason
   * over the whole workspace independent of the active room. Filters to this
   * collection + not-deleted defensively.
   */
  async listAll(): Promise<TaskUpdate[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASK_UPDATES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toUpdate(r));
  }

  /** Creates a new task update in a room. Seeds id/author/createdAt. */
  async create(roomId: string, draft: TaskUpdateDraft): Promise<TaskUpdate> {
    const identity = await this.api.identityProvider.getIdentity();
    const update: TaskUpdate = {
      id: taskUpdateDocId(),
      roomId,
      taskId: draft.taskId,
      author: identity.alias,
      completed: draft.completed?.trim() ?? '',
      next: draft.next?.trim() ?? '',
      blocker: draft.blocker?.trim() || undefined,
      status: draft.status,
      createdAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.save(update.id, update, this.indexAttributes(update));
    return update;
  }

  /** Soft-deletes a task update. */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: TaskUpdate = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
